import { styled } from "@mui/material";
import { api, types } from "api";
import { isUserWithProgress } from "api/types";
import { InfoSnackBar } from "components/InfoSnackBar";
import { ProfileLayout } from "components/ProfileLayout";
import { ProgressTable } from "components/ProgressTable";
import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import { ADD_ADMIN_FAIL, ERROR_MSG_PROFILE, REMOVE_ADMIN_FAIL } from "messages";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ProfilePageDetails } from "./ProfilePageDetails";
const PREFIX = "ProfilePage";

const classes = {
	Btn: `${PREFIX}-Btn`,
};

const Root = styled("div")(({ theme }) => ({
	[`& .${classes.Btn}`]: {
		padding: "10px 25px",
		marginTop: theme.spacing(1),
		marginRight: theme.spacing(1),
		float: "left",
	},
}));

export const ProfilePage = (): JSX.Element => {
	const { updateLastBreadcrumbs } = useBreadcrumbs();

	const [user, setUser] = useState<types.User | null>(null);
	const [err, setErr] = useState("");
	const params = useParams<{ userId: types.UUID }>();

	const fetchUser = useCallback(() => {
		const query = params.userId
			? api.getUserDetails(params.userId)
			: api.getMyUserData();
		query
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr("");
				setUser(res.data);

				if (params.userId) {
					updateLastBreadcrumbs({
						name: res.data.firstName + " " + res.data.lastName,
						params,
					});
				}
			})
			.catch(() => {
				setErr(ERROR_MSG_PROFILE);
			});
	}, [params, updateLastBreadcrumbs]);

	useEffect(() => {
		fetchUser();
	}, [fetchUser]);

	const toggleIsAdmin = (isUserAdmin: boolean): void => {
		if (user === null) {
			return;
		}

		const query = isUserAdmin
			? api.removeAdmin([user.userId])
			: api.addAdmin([user.userId]);
		query
			.then((res) => {
				if (res.status !== 200) {
					throw new Error(isUserAdmin ? REMOVE_ADMIN_FAIL : ADD_ADMIN_FAIL);
				}

				fetchUser();
			})
			.catch((e) => {
				setErr((e as unknown as string)?.toString());
			});
	};

	return (
		<Root>
			<ProfileLayout user={user}>
				{user && (
					<ProfilePageDetails
						user={user}
						toggleIsAdmin={toggleIsAdmin}
						isMe={params.userId === undefined}
					/>
				)}
			</ProfileLayout>

			{/* `getMyUserData` doesn't respond with `UserWithProgress` */}
			{user && isUserWithProgress(user) && user?.role.includes("student") && (
				<ProgressTable trackUser={user} />
			)}

			<InfoSnackBar
				isOpen={err !== ""}
				severity="error"
				onClose={() => setErr("")}
				message={err}
			/>
		</Root>
	);
};
